import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import './toolbarFilterPopup.css'

import { TfiClose } from "react-icons/tfi";
import { AppContext } from "../../context/AppContext";
import { openOptions } from "../../screen/HomeScreen/TopBar/topBar/topBarToolbar";
import { FilterValue, Restaurant } from "../types/types";
import { filterRestaurantsWithFilters } from "../../context/RestaurantContext";
import { FiInfo } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

const PRICE_MIN = 0;
const PRICE_MAX = 100;
const PRICE_STEP = 10;

interface ToolbarFilterPopupProps {
  filterType: string;
  onClose: () => void;
  hasNeighborHood: [string, string[][]][];

  getNeighborhoodResultCount: (neighborhoods: string[]) => number;
  toolbarRestaurants:Restaurant[]
}

const ToolbarFilterPopup: React.FC<ToolbarFilterPopupProps> = ({filterType,onClose,hasNeighborHood,getNeighborhoodResultCount,toolbarRestaurants}) => {
  const {filterChoose,setFilterChoose,setDate_selected, setHour_selected,date_selected,hour_selected,currentNeighborhood,setCurrentNeighborhood} = useContext(AppContext);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [draftNeighborhood, setDraftNeighborhood] = useState<string[]>([...currentNeighborhood]);
  
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsOpen(true);
    });
  }, []);
  const navigate = useNavigate()
  const location = useLocation();
    const navigateToAllRestaurantsIfEmpty = (nextFilters: FilterValue[],nextDate: Date | null,nextHour: string) => {
        if (nextFilters.length === 0 && !nextDate &&!nextHour && location.pathname === "/" ) navigate("/restaurants");
    };

  const handleClose = () => {
    setIsClosing(true);
    setIsOpen(false);
    setTimeout(() => {onClose()}, 70);
  };

  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    if (filterType !== "Ouvert") return [];

    return filterChoose.filter(
      (elem): elem is string =>
        typeof elem === "string" && openOptions.includes(elem)
    );
  });
  const currentPriceFilter = filterChoose.find(
    (elem): elem is [number, number] =>
      Array.isArray(elem) &&
      elem.length === 2 &&
      elem.every((num) => typeof num === "number")
  );

  const [minValue, setMinValue] = useState<number>(currentPriceFilter ? currentPriceFilter[0] : PRICE_MIN);
  const [maxValue, setMaxValue] = useState<number>(currentPriceFilter ? currentPriceFilter[1] : PRICE_MAX);
  const middle = (minValue + maxValue) / 2;
  const priceWrapperRef = useRef<HTMLDivElement | null>(null);
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value);
    if (value >= maxValue - PRICE_STEP) {
      value = maxValue - PRICE_STEP; 
    }
    setMinValue(value);
  };
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value);
    if (value <= minValue + PRICE_STEP) {
      value = minValue + PRICE_STEP; 
    }
    setMaxValue(value);
  };
  const updatePriceFromTouch = (e: React.TouchEvent<HTMLDivElement>,which: 'min' | 'max') => {
      e.stopPropagation();

      const wrapper = priceWrapperRef.current;
      if (!wrapper) return;

      const touch = e.touches[0];
      const rect = wrapper.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, x / rect.width));

      const raw = PRICE_MIN + ratio * (PRICE_MAX - PRICE_MIN);
      const clamped = Math.max(PRICE_MIN, Math.min(PRICE_MAX, raw));
      let stepped = Math.round(clamped / PRICE_STEP) * PRICE_STEP;

      if (which === 'min') {
          if (stepped >= maxValue - PRICE_STEP) {
              stepped = maxValue - PRICE_STEP; 
          }
          const nextMin = Math.min(stepped, maxValue);
          setMinValue(nextMin);
      } else {
          if (stepped <= minValue + PRICE_STEP) {
              stepped = minValue + PRICE_STEP; 
          }
          const nextMax = Math.max(stepped, minValue);
          setMaxValue(nextMax);
      }
  };

  const toggleValue = (value: string) => {
    setSelectedValues((prev) => prev.includes(value) ? [] : [value] );
  };

  const clearCurrentFilter = () => {
    if (filterType === "Ouvert") {
      setSelectedValues([]);
      return;
    }
    if (filterType === "Quartier") {
      setDraftNeighborhood([]);
      return;
    }
    if (filterType === "Prix") {
      setMinValue(PRICE_MIN);
      setMaxValue(PRICE_MAX);
    }
  };
  const hadOpenFilterInitially = useRef(
    filterChoose.some(
      (elem) =>
        typeof elem === "string" &&
        openOptions.includes(elem)
    )
  );
    const tempFilters = useMemo<FilterValue[]>(() => {
      let nextFilters = [...filterChoose];
      if (filterType === "Ouvert") {
          nextFilters = nextFilters.filter(elem => typeof elem !== "string" || !openOptions.includes(elem));
          return [...nextFilters, ...selectedValues];
      }
      if (filterType === "Prix") {
          nextFilters = nextFilters.filter(
              elem => !( Array.isArray(elem) && elem.length === 2 && elem.every(num => typeof num === "number"))
          );
          if (minValue === PRICE_MIN && maxValue === PRICE_MAX) return nextFilters;
          return [...nextFilters,[minValue, maxValue] as [number, number]];
      }
      return nextFilters;
  }, [filterChoose,filterType,selectedValues,minValue,maxValue]);
  const tempFilterResultCount = useMemo(() => {
      if (filterType !== "Ouvert" && filterType !== "Prix") {
          return null;
      }
      return filterRestaurantsWithFilters(toolbarRestaurants  ?? [],tempFilters).length;
  }, [filterType,toolbarRestaurants ,tempFilters]);
  const tempNeighborhoodResultCount = useMemo(() => {
      if (filterType !== "Quartier") return null;
      return getNeighborhoodResultCount(draftNeighborhood);
  }, [filterType,draftNeighborhood,getNeighborhoodResultCount]);
  const tempResultCount = filterType === "Quartier" ? tempNeighborhoodResultCount : tempFilterResultCount;

  const applyFilter = () => {
    if (filterType === "Ouvert") {
      const withoutOpen = filterChoose.filter((elem) => {
        if (typeof elem !== "string") return true;
        return !openOptions.includes(elem);
      });
      const nextFilters = [...withoutOpen, ...selectedValues];
      setFilterChoose(nextFilters);

      if(selectedValues && selectedValues[0]) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const currentHour = `${String(today.getHours()).padStart(2, "0")}h${String(
          today.getMinutes()
        ).padStart(2, "0")}`;
        if(selectedValues[0] === "Ouvert ce midi") {
          setDate_selected(today)
          setHour_selected("12h30")
        }
        if(selectedValues[0] === "Ouvert ce soir") {
          setDate_selected(today)
          setHour_selected("20h00")
        }
        if(selectedValues[0] === "Ouvert demain midi") {
          setDate_selected(tomorrow)
          setHour_selected("12h30")
        }
        if(selectedValues[0] === "Ouvert demain soir") {
          setDate_selected(tomorrow)
          setHour_selected("20h00")
        }
        if(selectedValues[0] === "Ouvert maintenant") {
          setDate_selected(today)
          setHour_selected(currentHour)
        }
      } else if (hadOpenFilterInitially.current) {
        setDate_selected(null);
        setHour_selected("");
        navigateToAllRestaurantsIfEmpty(nextFilters,null,"");
      }
    }
    if (filterType === "Quartier") {
      setCurrentNeighborhood(draftNeighborhood);
    }
    if (filterType === "Prix") {
       const withoutPrice = filterChoose.filter(elem =>
            !(Array.isArray(elem) &&elem.length === 2 &&elem.every(num => typeof num === "number") )
        );
        const nextFilters: FilterValue[] = minValue === PRICE_MIN && maxValue === PRICE_MAX ? withoutPrice : [...withoutPrice, [minValue, maxValue] as [number, number]];
        setFilterChoose(nextFilters);
        navigateToAllRestaurantsIfEmpty(nextFilters,date_selected,hour_selected);
    }

    handleClose();
  };
  const areAllNeighborhoodsSelected = (values: string[]) => {
    return (values.length > 0 && values.every((value) =>draftNeighborhood.includes(value)));
  };
  const toggleNeighborhood = (cantToggle:boolean,values: string[]) => {
    if(cantToggle) {
      setShowCantNeighborhood(true)
      setTimeout(() => {
          setShowCantNeighborhood(false)
      },5000)
      return;
    }
    setDraftNeighborhood(prev => 
      getNextNeighborhoodSelection(prev, values)
    );
  };
  const [showCantLunch,setShowCantLunch] = useState(false)
  const toggleLunch = () => {
    setShowCantLunch(true)
    setTimeout(() => {
        setShowCantLunch(false)
    },5000)
  };

  const popupContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const popup = popupContentRef.current;
    if (!popup || (filterType !== "Prix" && filterType !== "Ouvert")) return;

    let startX = 0;
    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {startX = e.touches[0].clientX;startY = e.touches[0].clientY;};
    const onTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      if (filterType === "Prix") {
        e.preventDefault();
        return;
      }
      const isScrollable = popup.scrollHeight > popup.clientHeight;
      if (!isScrollable) {
        e.preventDefault();
        return;
      }
      const isAtTop = popup.scrollTop <= 0;
      const isAtBottom = popup.scrollTop + popup.clientHeight >= popup.scrollHeight - 1;
      const tryingToScrollAbove = isAtTop && deltaY > 0;
      const tryingToScrollBelow = isAtBottom && deltaY < 0;

      if (tryingToScrollAbove || tryingToScrollBelow) {
        e.preventDefault();
      }
    };

    popup.addEventListener("touchstart", onTouchStart, { passive: true });
    popup.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      popup.removeEventListener("touchstart", onTouchStart);
      popup.removeEventListener("touchmove", onTouchMove);
    };
  }, [filterType]);
  const renderContent = () => {
    if (filterType === "Ouvert") {
      const now = new Date();
      const isLunchPast = now.getHours() > 14 || (now.getHours() === 14 && now.getMinutes() >= 30);
      return (
        <div className="toolbarPopupList smallText">
          {openOptions.map((option) => {
            const isLunchDisabled = option === "Ouvert ce midi" && isLunchPast;
            return <label key={option} style={isLunchDisabled ? { opacity: 0.5} : {}} className="toolbarPopupOption" onClick={() => {if (isLunchDisabled) {toggleLunch();return;}toggleValue(option)}}>
              <div className={`${selectedValues.includes(option) ? 'isSelect' : ''}`}></div>
              <span>{option}</span>
            </label>
          })}
        </div>
      );
    }

    if (filterType === "Quartier") {
      return (
        <div className="toolbarPopupList smallText">
          {hasNeighborHood.map(([sectionName, neighborhoodGroups]) => {
            const availableGroups = neighborhoodGroups.filter(group => {
              const isSelected = group.length > 0 && group.every(value =>     draftNeighborhood.includes(value) );
              return (isSelected ||getNeighborhoodResultCount(group) > 0);
          });
          const availableSectionValues = Array.from(new Set(availableGroups.flat()));
          const isSectionSelected = availableSectionValues.length > 0 && availableSectionValues.every(value =>     draftNeighborhood.includes(value) );
          const isSectionDisabled = availableGroups.length === 0;
            return <div key={sectionName} className="toolbarPopupNeighborhoodGroup">
              <label  className={`toolbarPopupOption neighborMain ${(isSectionDisabled && !isSectionSelected) ? "isLock" : ""}`} onClick={() => toggleNeighborhood((isSectionDisabled && !isSectionSelected),availableSectionValues)}>
                <div className={`${isSectionSelected ? "isSelect" : "" }`}></div>
                <span>{sectionName}</span>
              </label>

              {neighborhoodGroups.map((group) => {
                const isGroupSelected = areAllNeighborhoodsSelected(group);
                const groupLabel = group.join(" / ");
                const groupResultCount = getNeighborhoodResultCount(group);
                const isGroupDisabled = groupResultCount === 0;
                return <label key={`${sectionName}-${group.join("-")}`} className={`toolbarPopupOption scndList ${(isGroupDisabled && !isGroupSelected) ? 'isLock' : ''}`} onClick={() => toggleNeighborhood((isGroupDisabled && !isGroupSelected) ,group)}>
                  <div className={`${isGroupSelected  ? "isSelect" : "" }`}></div>
                  <span>{groupLabel}</span>
                </label>
              })}
            </div>
          })}
        </div>
      );
    }

    if (filterType === "Prix") {
      return (
        <div className="toolbarPopupPrice smallText">
            <div className="slideFilter slideDobble XsmallText" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}> 
                <div className="value flexAllCenter" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}>{minValue+'€'}</div>
                <div className="pointerValue" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}></div>
                <div className="value flexAllCenter" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 : maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}>{maxValue === 100 ? '100€+' : maxValue+'€'}</div>
                <div className="pointerValue" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 : maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}></div>

                <div className="patchClose" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}></div>
                <div className="range-wrapper" ref={priceWrapperRef}>
                    <div className="price-touch-zone price-touch-zone-min" style={{ left: 0, width: `${middle}%` }} onTouchStart={(e) => updatePriceFromTouch(e, 'min')} onTouchMove={(e) => updatePriceFromTouch(e, 'min')} />
                    <div className="price-touch-zone price-touch-zone-max" style={{ left: `${middle}%`, width: `${100 - middle}%` }} onTouchStart={(e) => updatePriceFromTouch(e, 'max')} onTouchMove={(e) => updatePriceFromTouch(e, 'max')} />
                    <input type="range" aria-label="Prix minimum en euros" id="minRange" min="0" max="100" step={PRICE_STEP} value={minValue} onChange={(e) => handleMinChange(e)}/>
                    <input type="range" aria-label="Prix maximum en euros" id="maxRange" min="0" max="100" step={PRICE_STEP} value={maxValue} onChange={(e) => handleMaxChange(e)}/>
                </div>
                
                <div className="slider-track" style={{left: `${Math.min(minValue, 88)}%`,width: `${Math.min(99, Math.max(11, maxValue - minValue))}%`}}></div>
                <div className="indicationPart XsmallText"><span>0 €</span> <span>100 € +</span></div>
            </div> 
        </div>
      );
    }

    return null;
  };

  const [showCantSearch,setShowCantSearch] = useState(false)
  const updateCantSearch = () => {
    setShowCantSearch(true)
    setTimeout(() => {
        setShowCantSearch(false)
    },5000)
  }
  const [showCantNeighborhood,setShowCantNeighborhood] = useState(false)

  const noSuggest = filterType === "Quartier"
    ? "Essayez d’enlever un ou plusieurs quartiers."
    : filterType === "Ouvert"
      ? "Essayez de choisir un autre horaire."
      : filterType === "Prix" ? "Essayez d’élargir votre fourchette de prix."
        : "Essayez d’enlever un ou plusieurs filtres."
  const widthNoSuggest = filterType === "Quartier" ? 270 : filterType === "Ouvert" ? 240 : 260

  const getNextNeighborhoodSelection = (current: string[],values: string[]) => {
      const areAllSelected = values.every(value => current.includes(value));
      if (areAllSelected) return current.filter(value => !values.includes(value));

      return Array.from(new Set([...current, ...values]));
  };

  const [isResultCountLoading, setIsResultCountLoading] = useState(false);
    useEffect(() => {
        setIsResultCountLoading(true);
        const timeout = window.setTimeout(() => {
            setIsResultCountLoading(false);
        }, 500);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [draftNeighborhood,minValue,maxValue,selectedValues]);

  return (
    <>
        <div className={`toolbarPopup primaryBack smallText ${filterType === "Quartier" ? 'canOverflow' : ''}  ${filterType === "Ouvert" ? 'isForOpen' : ''} ${isOpen && !isClosing ? "open" : ""} ${isClosing ? "closing" : ""}`}>
          <div className="toolbarPopupHeader">
            <span>{filterType}</span>
            <TfiClose className="close pointer" size={18} onClick={handleClose} />
            
            <div className="shadowTop"></div>

            {((showCantNeighborhood || showCantLunch) && !showCantSearch) && <div className={`containerCantSearch cantNeighboorhood smallText `} >
                <FiInfo className='icon' />
                  <span>Aucun résultat trouvé. {showCantLunch ? ` Essayez de choisir un autre horaire.` : ` Essayez d’enlever un ou plusieurs filtres.`}</span>
            </div>}
          </div>
          <div className="separationTop"><div className="shadowTop"></div></div>
          <div className={`toolbarPopupContent `} ref={popupContentRef}>
            {renderContent()}
          </div>

          <div className="toolbarPopupFooter">
            <div className="cleanButton" onClick={clearCurrentFilter}>
              Tout effacer
            </div>
            <div className={` midGoldRoundButton  ${(tempResultCount ?? 0) === 0 ? "isLock" : ""}`} onClick={() => {if((tempResultCount ?? 0) === 0){updateCantSearch()}else{applyFilter()}}}>
              {isResultCountLoading ? <span className="countSpinnerLoad" /> :  <>Afficher <span>{` (${tempResultCount ?? 0} résultat${(tempResultCount ?? 0) > 1 ? "s" : ""})`}</span></>}
            </div>
            {showCantSearch && <div className={`containerCantSearch smallText `} style={{maxWidth:`${widthNoSuggest}px`}}>
                  <FiInfo className='icon' />
                    <span>Aucun résultat trouvé. {noSuggest}</span>
              </div>}
          </div>
        </div>
        
        <div className="maskBackToolbarPopup" onClick={handleClose}></div>
    </>
  );
};

export default ToolbarFilterPopup;